import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PortHistoryRecord {
  id: string;
  timestamp: string;
  deviceId: number;
  deviceName: string;
  deviceIp: string;
  port1Status: boolean;
  port2Status: boolean;
  port3Status: boolean;
  port4Status: boolean;
  portsUp: number;
  portsDown: number;
  responseTime: number;
}

interface KavachHistoryGraphProps {
  data: PortHistoryRecord[];
  deviceName?: string;
}

const KavachHistoryGraph: React.FC<KavachHistoryGraphProps> = ({ data, deviceName }) => {
  // Process data for charts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Sort data by timestamp
    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Group data by hour for timeline
    const hourlyData: Map<
      string,
      {
        healthy: number;
        partial: number;
        critical: number;
        total: number;
        avgResponseTime: number;
        responseTimeSum: number;
        port1Up: number;
        port2Up: number;
        port3Up: number;
        port4Up: number;
      }
    > = new Map();

    sortedData.forEach((record) => {
      const hourKey = dayjs(record.timestamp).format('DD/MM HH:00');
      const existing = hourlyData.get(hourKey) || {
        healthy: 0,
        partial: 0,
        critical: 0,
        total: 0,
        avgResponseTime: 0,
        responseTimeSum: 0,
        port1Up: 0,
        port2Up: 0,
        port3Up: 0,
        port4Up: 0,
      };

      existing.total++;
      existing.responseTimeSum += record.responseTime;

      // Count by status
      if (record.portsUp === 4) {
        existing.healthy++;
      } else if (record.portsUp >= 2) {
        existing.partial++;
      } else {
        existing.critical++;
      }

      // Count individual port status
      if (record.port1Status) existing.port1Up++;
      if (record.port2Status) existing.port2Up++;
      if (record.port3Status) existing.port3Up++;
      if (record.port4Status) existing.port4Up++;

      hourlyData.set(hourKey, existing);
    });

    const labels = Array.from(hourlyData.keys());
    const healthyData = Array.from(hourlyData.values()).map((v) => v.healthy);
    const partialData = Array.from(hourlyData.values()).map((v) => v.partial);
    const criticalData = Array.from(hourlyData.values()).map((v) => v.critical);
    const uptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.healthy / v.total) * 100 : 0
    );
    const responseTimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? v.responseTimeSum / v.total : 0
    );
    const port1UptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.port1Up / v.total) * 100 : 0
    );
    const port2UptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.port2Up / v.total) * 100 : 0
    );
    const port3UptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.port3Up / v.total) * 100 : 0
    );
    const port4UptimeData = Array.from(hourlyData.values()).map((v) =>
      v.total > 0 ? (v.port4Up / v.total) * 100 : 0
    );

    return {
      labels,
      healthyData,
      partialData,
      criticalData,
      uptimeData,
      responseTimeData,
      port1UptimeData,
      port2UptimeData,
      port3UptimeData,
      port4UptimeData,
    };
  }, [data]);

  if (!chartData) {
    return (
      <div className="no-graph-data">
        <p>No data available for graph visualization.</p>
      </div>
    );
  }

  // Overall Health Timeline Chart (Line)
  const uptimeChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'All Ports Up %',
        data: chartData.uptimeData,
        fill: true,
        borderColor: '#27ae60',
        backgroundColor: 'rgba(39, 174, 96, 0.1)',
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: '#27ae60',
      },
    ],
  };

  const uptimeOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: `Switch Health Over Time${deviceName ? ` - ${deviceName}` : ''}`,
        font: { size: 14, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: (context) => `Health: ${(context.raw as number)?.toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Health %',
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Port Uptime Chart (Line - multiple lines)
  const portUptimeChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Port 1',
        data: chartData.port1UptimeData,
        borderColor: '#3498db',
        backgroundColor: 'rgba(52, 152, 219, 0.1)',
        tension: 0.4,
        pointRadius: 2,
      },
      {
        label: 'Port 2',
        data: chartData.port2UptimeData,
        borderColor: '#9b59b6',
        backgroundColor: 'rgba(155, 89, 182, 0.1)',
        tension: 0.4,
        pointRadius: 2,
      },
      {
        label: 'Port 3',
        data: chartData.port3UptimeData,
        borderColor: '#f39c12',
        backgroundColor: 'rgba(243, 156, 18, 0.1)',
        tension: 0.4,
        pointRadius: 2,
      },
      {
        label: 'Port 4',
        data: chartData.port4UptimeData,
        borderColor: '#1abc9c',
        backgroundColor: 'rgba(26, 188, 156, 0.1)',
        tension: 0.4,
        pointRadius: 2,
      },
    ],
  };

  const portUptimeOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Port Uptime by Port',
        font: { size: 14, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${(context.raw as number)?.toFixed(1)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Uptime %',
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Status Distribution Chart (Stacked Bar)
  const statusBarData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'All Up',
        data: chartData.healthyData,
        backgroundColor: 'rgba(39, 174, 96, 0.8)',
        borderColor: '#27ae60',
        borderWidth: 1,
      },
      {
        label: 'Partial',
        data: chartData.partialData,
        backgroundColor: 'rgba(243, 156, 18, 0.8)',
        borderColor: '#f39c12',
        borderWidth: 1,
      },
      {
        label: 'Critical',
        data: chartData.criticalData,
        backgroundColor: 'rgba(231, 76, 60, 0.8)',
        borderColor: '#e74c3c',
        borderWidth: 1,
      },
    ],
  };

  const statusBarOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      title: {
        display: true,
        text: 'Status Distribution Over Time',
        font: { size: 14, weight: 'bold' },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        title: {
          display: true,
          text: 'Count',
        },
      },
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  // Calculate summary stats
  const totalRecords = data.length;
  const healthyRecords = data.filter((r) => r.portsUp === 4).length;
  const partialRecords = data.filter((r) => r.portsUp >= 2 && r.portsUp < 4).length;
  const criticalRecords = data.filter((r) => r.portsUp < 2).length;
  const avgResponseTime =
    data.reduce((sum, r) => sum + r.responseTime, 0) / (totalRecords || 1);

  return (
    <div className="history-graphs">
      <div className="graph-grid">
        <div className="graph-card">
          <div className="graph-container" style={{ height: '280px' }}>
            <Line data={uptimeChartData} options={uptimeOptions} />
          </div>
        </div>
        <div className="graph-card">
          <div className="graph-container" style={{ height: '280px' }}>
            <Line data={portUptimeChartData} options={portUptimeOptions} />
          </div>
        </div>
        <div className="graph-card full-width">
          <div className="graph-container" style={{ height: '280px' }}>
            <Bar data={statusBarData} options={statusBarOptions} />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="graph-summary">
        <div className="summary-item">
          <span className="summary-label">Total Records</span>
          <span className="summary-value">{totalRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">All Up</span>
          <span className="summary-value success">{healthyRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Partial</span>
          <span className="summary-value warning">{partialRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Critical</span>
          <span className="summary-value danger">{criticalRecords}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Health</span>
          <span className="summary-value">
            {totalRecords > 0 ? ((healthyRecords / totalRecords) * 100).toFixed(1) : 0}%
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Response</span>
          <span className="summary-value">{avgResponseTime.toFixed(0)} ms</span>
        </div>
      </div>
    </div>
  );
};

export default KavachHistoryGraph;
